import { visualGroupFixtures } from "./visualGroupFixtures";
import type { ConjointRaw } from "../questions/conjoint/core";
import type { EmbedRaw } from "../questions/embed/core";
import type { GridMultiSelectRaw } from "../questions/gridMultiSelect/core";
import type { GridNumericRaw } from "../questions/gridNumeric/core";
import type { GridQuestionGroupRaw } from "../questions/gridQuestionGroup/core";
import type { GridRatingRaw } from "../questions/gridRating/core";
import type { GridSelectRaw } from "../questions/gridSelect/core";
import type { GridThisOrThatRaw } from "../questions/gridThisOrThat/core";
import type { GridThisOrThatRatingRaw } from "../questions/gridThisOrThatRating/core";
import type { ImageRaw } from "../questions/image/core";
import type { MaxDiffRaw } from "../questions/maxDiff/core";
import type { MultiSelectRaw } from "../questions/multiSelect/core";
import type { NumericRaw } from "../questions/numeric/core";
import type { PhoneRaw } from "../questions/phone/core";
import type { QuestionGroupRaw } from "../questions/questionGroup/core";
import type { RankSortRaw } from "../questions/rankSort/core";
import type { RatingRaw } from "../questions/rating/core";
import type { TextRaw } from "../questions/text/core";
import type { TextHighlighterRaw } from "../questions/textHighlighter/core";
import type { TextListRaw } from "../questions/textList/core";
import type { VideoRaw } from "../questions/video/core";
import type { SurveyAnswer } from "../session/transport";
import type { SelectTypes } from "../util/types";
import questionGroupRequest from "./fixtures/question-group.json";
import gridQuestionGroupRequest from "./fixtures/grid-question-group.json";

type GalleryFixtureBase = {
  id: string;
  description: string;
  storedValues?: Record<string, string>;
  expected: {
    response?: SurveyAnswer;
  };
  features: readonly string[];
};

export type SelectGalleryFixture = GalleryFixtureBase & {
  type: "select";
  request: Omit<SelectTypes, "style" | "previous_response">;
  previousAnswer?: SelectTypes["previous_response"];
};

export type TextListGalleryFixture = GalleryFixtureBase & {
  type: "text_list";
  request: Omit<TextListRaw, "previous_response">;
  previousAnswer?: string[];
};

export type TextGalleryFixture = GalleryFixtureBase & {
  type: "text";
  request: Omit<TextRaw, "previous_response">;
  previousAnswer?: string;
};

export type NumericGalleryFixture = GalleryFixtureBase & {
  type: "numeric";
  request: Omit<NumericRaw, "previous_response">;
  previousAnswer?: string;
};

export type PhoneGalleryFixture = GalleryFixtureBase & {
  type: "phone_number";
  request: Omit<PhoneRaw, "previous_response">;
  previousAnswer?: string;
};

export type MultiSelectGalleryFixture = GalleryFixtureBase & {
  type: "multi-select";
  request: Omit<MultiSelectRaw, "style" | "previous_response">;
  previousAnswer?: MultiSelectRaw["previous_response"];
};

export type RatingGalleryFixture = GalleryFixtureBase & {
  type: "rating";
  request: Omit<RatingRaw, "style" | "previous_response">;
  previousAnswer?: string | number;
};

export type GridSelectGalleryFixture = GalleryFixtureBase & {
  type: "grid_select";
  request: Omit<GridSelectRaw, "style" | "previous_response">;
  previousAnswer?: GridSelectRaw["previous_response"];
};

export type GridMultiSelectGalleryFixture = GalleryFixtureBase & {
  type: "grid_multi_select";
  request: Omit<GridMultiSelectRaw, "style" | "previous_response">;
  previousAnswer?: GridMultiSelectRaw["previous_response"];
};

export type GridRatingGalleryFixture = GalleryFixtureBase & {
  type: "grid_rating";
  request: Omit<GridRatingRaw, "style" | "previous_response">;
  previousAnswer?: GridRatingRaw["previous_response"];
};

export type GridNumericGalleryFixture = GalleryFixtureBase & {
  type: "grid_numeric";
  request: Omit<GridNumericRaw, "previous_response">;
  previousAnswer?: GridNumericRaw["previous_response"];
};

export type RankSortGalleryFixture = GalleryFixtureBase & {
  type: "rank_sort";
  request: Omit<RankSortRaw, "style" | "previous_response">;
  previousAnswer?: string[];
};

export type MaxDiffGalleryFixture = GalleryFixtureBase & {
  type: "max_diff";
  request: Omit<MaxDiffRaw, "previous_response">;
  previousAnswer?: string[];
};

export type GridThisOrThatGalleryFixture = GalleryFixtureBase & {
  type: "grid_this_or_that";
  request: Omit<GridThisOrThatRaw, "previous_response">;
  previousAnswer?: string[];
};

export type GridThisOrThatRatingGalleryFixture = GalleryFixtureBase & {
  type: "grid_this_or_that_rating";
  request: Omit<GridThisOrThatRatingRaw, "style" | "previous_response">;
  previousAnswer?: (string | number)[];
};

export type ConjointGalleryFixture = GalleryFixtureBase & {
  type: "conjoint";
  request: Omit<ConjointRaw, "previous_response">;
  previousAnswer?: string;
};

export type TextHighlighterGalleryFixture = GalleryFixtureBase & {
  type: "text_highlighter";
  request: Omit<TextHighlighterRaw, "previous_response">;
  previousAnswer?: string[] | string;
};

export type ImageGalleryFixture = GalleryFixtureBase & {
  type: "image";
  request: ImageRaw;
  previousAnswer?: never;
};

export type VideoGalleryFixture = GalleryFixtureBase & {
  type: "video";
  request: VideoRaw;
  previousAnswer?: never;
};

export type EmbedGalleryFixture = GalleryFixtureBase & {
  type: "embed";
  request: EmbedRaw;
  previousAnswer?: never;
};

export type QuestionGroupGalleryFixture = GalleryFixtureBase & {
  type: "question_group";
  request: QuestionGroupRaw;
  previousAnswer?: never;
};

export type GridQuestionGroupGalleryFixture = GalleryFixtureBase & {
  type: "grid_question_group";
  request: GridQuestionGroupRaw;
  previousAnswer?: never;
};

export type GalleryFixture =
  | SelectGalleryFixture
  | TextListGalleryFixture
  | TextGalleryFixture
  | NumericGalleryFixture
  | PhoneGalleryFixture
  | MultiSelectGalleryFixture
  | RatingGalleryFixture
  | GridSelectGalleryFixture
  | GridMultiSelectGalleryFixture
  | GridRatingGalleryFixture
  | GridNumericGalleryFixture
  | RankSortGalleryFixture
  | MaxDiffGalleryFixture
  | GridThisOrThatGalleryFixture
  | GridThisOrThatRatingGalleryFixture
  | ConjointGalleryFixture
  | TextHighlighterGalleryFixture
  | ImageGalleryFixture
  | VideoGalleryFixture
  | EmbedGalleryFixture
  | QuestionGroupGalleryFixture
  | GridQuestionGroupGalleryFixture;

export const selectGalleryRequest = (
  fixture: SelectGalleryFixture,
  view: string,
  previousAnswer: boolean,
): SelectTypes => {
  if (previousAnswer && fixture.previousAnswer === undefined) {
    throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
  }
  return {
    ...fixture.request,
    style: view === "buttons" ? "button" : view,
    ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
  };
};

export const galleryQuestionRequest = (
  fixture: GalleryFixture,
  view: string,
  previousAnswer: boolean,
):
  | SelectTypes
  | TextListRaw
  | TextRaw
  | NumericRaw
  | PhoneRaw
  | MultiSelectRaw
  | RatingRaw
  | GridSelectRaw
  | GridMultiSelectRaw
  | GridRatingRaw
  | GridNumericRaw
  | RankSortRaw
  | MaxDiffRaw
  | GridThisOrThatRaw
  | GridThisOrThatRatingRaw
  | ConjointRaw
  | TextHighlighterRaw
  | ImageRaw
  | VideoRaw
  | EmbedRaw
  | QuestionGroupRaw
  | GridQuestionGroupRaw => {
  if (fixture.type === "select") {
    return selectGalleryRequest(fixture, view, previousAnswer);
  }
  if (fixture.type === "multi-select") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      style: view === "buttons" ? "button" : view,
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "rating") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      style: view === "buttons" ? "button" : (view as RatingRaw["style"]),
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "grid_select") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      style: view === "buttons" ? "button" : (view as GridSelectRaw["style"]),
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "grid_multi_select") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      style:
        view === "buttons" ? "button" : (view as GridMultiSelectRaw["style"]),
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "grid_rating") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      style: view === "buttons" ? "button" : (view as GridRatingRaw["style"]),
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "grid_numeric") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "rank_sort") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      style: view === "buttons" ? "button" : "list",
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "max_diff") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "grid_this_or_that") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "grid_this_or_that_rating") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      style: view === "buttons" ? "button" : "slider",
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "conjoint") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "text_highlighter") {
    if (previousAnswer && fixture.previousAnswer === undefined) {
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    }
    return {
      ...fixture.request,
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  if (fixture.type === "image") {
    if (previousAnswer)
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    return fixture.request;
  }
  if (fixture.type === "video") {
    if (previousAnswer)
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    return fixture.request;
  }
  if (fixture.type === "embed") {
    if (previousAnswer)
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    return fixture.request;
  }
  if (
    fixture.type === "question_group" ||
    fixture.type === "grid_question_group"
  ) {
    if (previousAnswer)
      throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
    return fixture.request;
  }
  if (previousAnswer && fixture.previousAnswer === undefined) {
    throw new Error(`Fixture '${fixture.id}' has no previous answer.`);
  }
  if (fixture.type === "text_list") {
    return {
      ...fixture.request,
      ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
    };
  }
  return {
    ...fixture.request,
    ...(previousAnswer ? { previous_response: fixture.previousAnswer } : {}),
  };
};

const softDrinkImages = {
  cocaCola: "/gallery/soft-drinks/coca-cola.png",
  pepsi: "/gallery/soft-drinks/pepsi.png",
  canadaDry: "/gallery/soft-drinks/canada-dry.png",
  mtnDew: "/gallery/soft-drinks/mtn-dew.png",
  brisk: "/gallery/soft-drinks/brisk.png",
  drPepper: "/gallery/soft-drinks/dr-pepper.png",
  dietCoke: "/gallery/soft-drinks/diet-coke.png",
  sprite: "/gallery/soft-drinks/sprite.png",
} as const;
const partyImage = "/gallery/soft-drinks/party.png";
const drinkComparisonsImage = "/gallery/soft-drinks/drink-comparisons.png";

const exampleVideoUrl = "https://streams.dev.mx8.io/public/test/test.m3u8";

export const galleryFixtures: readonly GalleryFixture[] = [
  {
    id: "select-baseline",
    description: "Choose a soft drink",
    type: "select",
    request: {
      type: "select",
      content: "Which cola would you buy today?",
      options: [
        { value: 10, text: "Coca-Cola", disabled: false, display_text: true },
        { value: 30, text: "Pepsi", disabled: false, display_text: true },
      ],
    },
    previousAnswer: 30,
    expected: {
      response: { answerJson: { option: 10 }, type: "select", value: 10 },
    },
    features: ["baseline", "non-contiguous-ids", "restored"],
  },
  {
    id: "select-images",
    description: "Choose between pictured soft drinks",
    type: "select",
    request: {
      type: "select",
      content: "Which drink would you choose with lunch?",
      options: [
        {
          value: 10,
          text: "Diet Coke",
          disabled: true,
          display_text: true,
          image: softDrinkImages.dietCoke,
        },
        {
          value: 20,
          text: "Pepsi",
          disabled: false,
          display_text: true,
          image: softDrinkImages.pepsi,
        },
      ],
    },
    previousAnswer: 20,
    expected: {
      response: { answerJson: { option: 20 }, type: "select", value: 20 },
    },
    features: ["images", "disabled"],
  },
  {
    id: "select-stored-values",
    description: "Recall a previous soft drink choice",
    type: "select",
    request: {
      type: "select",
      content: "Which cola would you buy today?",
      options: [
        { value: 10, text: "Coca-Cola", disabled: false, display_text: true },
        { value: 20, text: "Pepsi", disabled: false, display_text: true },
      ],
    },
    storedValues: { Earlier: "Canada Dry", Second: "Sprite" },
    expected: {
      response: { answerJson: { option: 10 }, type: "select", value: 10 },
    },
    features: ["stored-values", "undo"],
  },
  {
    id: "multi-select-baseline",
    description: "Soft drinks bought in the past month",
    type: "multi-select",
    request: {
      type: "multi-select",
      content:
        "Which soft drinks have you bought in the past month? Select up to two.",
      max_options: 2,
      options: [
        {
          value: 1,
          text: "Coca-Cola",
          disabled: false,
          unique: false,
          display_text: true,
        },
        {
          value: 2,
          text: "Pepsi",
          disabled: false,
          unique: false,
          display_text: true,
        },
        {
          value: 3,
          text: "None of these",
          disabled: false,
          unique: true,
          display_text: true,
        },
        {
          value: 4,
          text: "Other",
          disabled: false,
          unique: false,
          display_text: true,
          pop_up_text: "Which other soft drink?",
        },
        {
          value: 5,
          text: "Brisk",
          disabled: true,
          unique: false,
          display_text: true,
          image: softDrinkImages.brisk,
        },
      ],
    },
    previousAnswer: [2, "Fanta"],
    expected: {
      response: {
        answerJson: [{ option: 1 }, { option: 2 }],
        type: "multi-select",
        value: "1,2",
        responses: "1\t2",
      },
    },
    features: [
      "baseline",
      "limits",
      "exclusive",
      "custom-text",
      "disabled",
      "images",
      "restored",
    ],
  },
  {
    id: "multi-select-unique-specify",
    description: "Specify a different cola brand instead",
    type: "multi-select",
    request: {
      type: "multi-select",
      content: "Which cola brands would you buy this week? Select up to two.",
      max_options: 2,
      options: [
        {
          value: 1,
          text: "Coca-Cola",
          disabled: false,
          unique: false,
          display_text: true,
        },
        {
          value: 2,
          text: "Pepsi",
          disabled: false,
          unique: false,
          display_text: true,
        },
        {
          value: 3,
          text: "A different cola brand instead",
          disabled: false,
          unique: true,
          display_text: true,
          pop_up_text: "Which cola brand?",
        },
      ],
    },
    previousAnswer: ["RC Cola"],
    expected: {
      response: {
        answerJson: [{ custom: "RC Cola" }],
        type: "multi-select",
        value: "3",
        responses: "RC Cola",
      },
    },
    features: ["exclusive", "custom-text", "restored"],
  },
  {
    id: "rating-baseline",
    description: "Rate a familiar cola",
    type: "rating",
    request: {
      type: "rating",
      content: "How much do you like the taste of Coca-Cola?",
      image: softDrinkImages.cocaCola,
      alt_text: "Coca-Cola can",
      options: [
        { value: 3, text: "Not at all", display_text: true },
        { value: 4, text: "", display_text: false },
        { value: 5, text: "Neutral", display_text: true },
        { value: 6, text: "", display_text: false },
        { value: 7, text: "Very much", display_text: true },
      ],
      dont_know_option: { value: "Don't know", text: "Don't know" },
    },
    previousAnswer: "5",
    expected: { response: { type: "rating", value: "5" } },
    features: [
      "baseline",
      "labels",
      "images",
      "restored",
      "dont-know",
      "unanswered",
      "non-one-based",
    ],
  },
  {
    id: "grid-select-baseline",
    description: "One soft drink for each occasion",
    type: "grid_select",
    request: {
      type: "grid_select",
      content: "Which soft drink would you choose for each occasion?",
      options: [
        { value: 1, text: "Coca-Cola", disabled: false },
        { value: 2, text: "Pepsi", disabled: false },
        { value: 3, text: "Brisk", disabled: true },
        {
          value: 4,
          text: "Other",
          disabled: false,
          pop_up_text: "Which other soft drink?",
        },
      ],
      rows: [
        { value: "lunch", text: "With lunch", display_text: true },
        {
          value: "party",
          text: "At a party",
          display_text: true,
          image: partyImage,
        },
      ],
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    previousAnswer: [1, "Fanta"],
    expected: {
      response: {
        answerJson: [{ option: 1 }, { option: 2 }],
        type: "grid_select",
        value: "1,2",
        responses: "1\t2",
      },
    },
    features: [
      "baseline",
      "rows",
      "images",
      "row-images",
      "disabled",
      "custom-text",
      "restored",
      "dont-know",
    ],
  },
  {
    id: "grid-multi-select-baseline",
    description: "Soft drinks considered for each occasion",
    type: "grid_multi_select",
    request: {
      type: "grid_multi_select",
      content: "Which soft drinks would you consider for each occasion?",
      options: [
        { value: 1, text: "Coca-Cola", disabled: false },
        { value: 2, text: "Pepsi", disabled: false },
        { value: 3, text: "Brisk", disabled: true },
        {
          value: 4,
          text: "Other",
          disabled: false,
          pop_up_text: "Which other soft drink?",
        },
        { value: 5, text: "None", disabled: false, unique: true },
      ],
      rows: [
        { value: "lunch", text: "With lunch", display_text: true },
        {
          value: "party",
          text: "At a party",
          display_text: true,
          image: partyImage,
        },
      ],
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    previousAnswer: ["1,Fanta", "2"],
    expected: {
      response: {
        answerJson: [[{ option: 1 }, { option: 2 }], [{ option: 2 }]],
        type: "grid_multi_select",
        value: "1,2|2",
        responses: "1,2\t2",
      },
    },
    features: [
      "baseline",
      "rows",
      "images",
      "row-images",
      "disabled",
      "exclusive",
      "custom-text",
      "restored",
      "dont-know",
    ],
  },
  {
    id: "grid-rating-baseline",
    description: "Rate Sprite for two occasions",
    type: "grid_rating",
    request: {
      type: "grid_rating",
      content: "How well would Sprite fit each occasion?",
      options: [
        { value: 0, text: "Not at all", display_text: true },
        { value: 1, text: "", display_text: false },
        { value: 2, text: "Very well", display_text: true },
      ],
      dont_know_option: { value: "Don't know", text: "Don't know" },
      rows: [
        { value: "lunch", text: "With lunch", display_text: true },
        {
          value: "party",
          text: "At a party",
          display_text: true,
          image: partyImage,
        },
      ],
    },
    previousAnswer: ["0", "Don't know"],
    expected: {
      response: { type: "grid_rating", value: "0,2", responses: "0\t2" },
    },
    features: [
      "baseline",
      "rows",
      "labels",
      "images",
      "row-images",
      "restored",
      "dont-know",
      "unanswered",
      "non-one-based",
    ],
  },
  {
    id: "grid-rating-long-labels",
    description: "Rate games with long familiarity labels",
    type: "grid_rating",
    request: {
      type: "grid_rating",
      content: "How familiar are you with each game?",
      options: [
        {
          value: 1,
          text: "Have heard of it but know nothing about it",
          display_text: true,
        },
        { value: 2, text: "Know a little about it", display_text: true },
        { value: 3, text: "Know a fair amount about it", display_text: true },
        {
          value: 4,
          text: "Very familiar -- I know it well",
          display_text: true,
        },
      ],
      dont_know_option: null,
      rows: [
        {
          value: "ark",
          text: "ARK: Survival Evolved / Survival Ascended",
          display_text: true,
        },
      ],
    },
    expected: {},
    features: ["long-labels"],
  },
  {
    id: "grid-rating-long-sparse-labels",
    description: "Rate games with sparse long familiarity labels",
    type: "grid_rating",
    request: {
      type: "grid_rating",
      content: "How familiar are you with each game?",
      options: [
        {
          value: 1,
          text: "Have heard of it but know nothing about it",
          display_text: true,
        },
        { value: 2, text: "", display_text: false },
        { value: 3, text: "", display_text: false },
        { value: 4, text: "", display_text: false },
        {
          value: 5,
          text: "Very familiar -- I know it well",
          display_text: true,
        },
      ],
      dont_know_option: null,
      rows: [
        {
          value: "ark",
          text: "ARK: Survival Evolved / Survival Ascended",
          display_text: true,
        },
      ],
    },
    expected: {},
    features: ["long-labels", "sparse-labels"],
  },
  {
    id: "grid-numeric-baseline",
    description: "Cans consumed by occasion and day",
    type: "grid_numeric",
    request: {
      type: "grid_numeric",
      content:
        "How many cans of soft drink did you have on each occasion last week?",
      columns: ["Weekdays", "Weekend"],
      rows: [
        { value: "lunch", text: "With lunch", display_text: true },
        {
          value: "party",
          text: "At a party",
          display_text: true,
          image: partyImage,
        },
      ],
      autosum_rows: true,
      autosum_columns: false,
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    previousAnswer: ["0,2", "3,4"],
    expected: {
      response: {
        type: "grid_numeric",
        value: "0,2,3,4",
        responses: "0,2\t3,4",
      },
    },
    features: [
      "baseline",
      "rows",
      "columns",
      "autosum-rows",
      "images",
      "row-images",
      "restored",
      "blank-zero",
      "dont-know",
    ],
  },
  {
    id: "grid-numeric-column-totals",
    description: "Cans consumed by brand and day",
    type: "grid_numeric",
    request: {
      type: "grid_numeric",
      content: "How many cans of each drink did you have last week?",
      columns: ["Weekdays", "Weekend"],
      rows: [
        { value: "coca-cola", text: "Coca-Cola", display_text: true },
        { value: "pepsi", text: "Pepsi", display_text: true },
      ],
      autosum_rows: false,
      autosum_columns: true,
    },
    previousAnswer: ["0,2", "3,4"],
    expected: {
      response: {
        type: "grid_numeric",
        value: "0,2,3,4",
        responses: "0,2\t3,4",
      },
    },
    features: ["autosum-columns", "restored"],
  },
  {
    id: "grid-numeric-single-column",
    description: "Cans bought by brand with an explicit Count column",
    type: "grid_numeric",
    request: {
      type: "grid_numeric",
      content: "How many cans of each drink did you buy last week?",
      columns: ["Count"],
      rows: [
        { value: "coca-cola", text: "Coca-Cola", display_text: true },
        { value: "pepsi", text: "Pepsi", display_text: true },
      ],
      prefill: { "coca-cola": { Count: 0 }, pepsi: { Count: 2 } },
      autosum_columns: true,
    },
    previousAnswer: [["0", "2"]],
    expected: {
      response: { type: "grid_numeric", value: "0,2", responses: "0,2" },
    },
    features: ["columns", "restored", "blank-zero"],
  },
  {
    id: "grid-numeric-list",
    description: "Cans bought by brand",
    type: "grid_numeric",
    request: {
      type: "grid_numeric",
      content: "How many cans of each drink did you buy last week?",
      rows: [
        { value: "coca-cola", text: "Coca-Cola", display_text: true },
        { value: "pepsi", text: "Pepsi", display_text: true },
      ],
      autosum_columns: true,
      autosum_rows: false,
      prefill: { "coca-cola": 0, pepsi: 2 },
    },
    previousAnswer: ["0", "2"],
    expected: {
      response: { type: "grid_numeric", value: "0,2", responses: "0\t2" },
    },
    features: ["numeric-list", "restored"],
  },
  {
    id: "rank-sort-baseline",
    description: "Rank drinks by purchase preference",
    type: "rank_sort",
    request: {
      type: "rank_sort",
      content: "Rank up to three drinks you would be most likely to buy.",
      labels: ["Least likely", "Most likely"],
      options: [
        { value: 1, text: "Coca-Cola", display_text: true, disabled: false },
        { value: 2, text: "Pepsi", display_text: true, disabled: false },
        {
          value: 3,
          text: "Sprite",
          display_text: true,
          disabled: false,
          image: softDrinkImages.sprite,
        },
        {
          value: 4,
          text: "Brisk",
          display_text: true,
          disabled: true,
        },
        {
          value: 5,
          text: "Canada Dry",
          display_text: true,
          disabled: false,
          image: softDrinkImages.canadaDry,
        },
        {
          value: 6,
          text: "Mtn Dew",
          display_text: true,
          disabled: false,
          image: softDrinkImages.mtnDew,
        },
      ],
      min_options: 2,
      max_options: 3,
      dont_know_option: { value: "Don't know", text: "Don't know" },
    },
    previousAnswer: ["3", "1"],
    expected: {
      response: { type: "rank_sort", value: "3,1", responses: "3\t1" },
    },
    features: [
      "baseline",
      "limits",
      "labels",
      "images",
      "option-images",
      "disabled",
      "restored",
      "dont-know",
    ],
  },
  {
    id: "rank-sort-mixed-labels",
    description: "Rank drinks with mixed label lengths and formatting",
    type: "rank_sort",
    request: {
      type: "rank_sort",
      content: "Rank these reasons for choosing a drink.",
      labels: ["Least likely", "Most likely"],
      options: [
        {
          value: 1,
          text: "A familiar flavour",
          display_text: true,
          disabled: false,
          image: softDrinkImages.sprite,
        },
        {
          value: 2,
          text: "A refreshing drink with a familiar flavour that is easy to find in local shops and comes in a convenient size for sharing",
          display_text: true,
          disabled: false,
        },
        {
          value: 3,
          text: "Good value for an everyday drink that is refreshing and convenient to take with you on the go",
          display_text: true,
          disabled: false,
        },
        {
          value: 4,
          text: "A convenient size\nfor drinking on the go",
          display_text: true,
          disabled: false,
        },
      ],
      min_options: 4,
      max_options: 4,
      dont_know_option: null,
    },
    previousAnswer: ["1", "2", "3", "4"],
    expected: {
      response: {
        type: "rank_sort",
        value: "1,2,3,4",
        responses: "1\t2\t3\t4",
      },
    },
    features: ["labels", "restored", "images", "option-images"],
  },
  {
    id: "max-diff-baseline",
    description: "Least and most appealing soft drinks",
    type: "max_diff",
    request: {
      type: "max_diff",
      content: "Which drink would you be least and most likely to buy?",
      options: [
        { value: "left", text: "Least", disabled: false },
        { value: "right", text: "Most", disabled: false },
      ],
      rows: [
        { value: "coca-cola", text: "Coca-Cola", display_text: true },
        {
          value: "pepsi",
          text: "Pepsi",
          display_text: true,
          image: softDrinkImages.pepsi,
        },
        {
          value: "dr-pepper",
          text: "Dr Pepper",
          display_text: true,
          image: softDrinkImages.drPepper,
        },
        { value: "sprite", text: "Sprite", display_text: true },
      ],
      dont_know_option: { value: "Don't know", text: "Don't know" },
    },
    previousAnswer: ["coca-cola", "sprite"],
    expected: {
      response: {
        type: "max_diff",
        value: "coca-cola,sprite",
        responses: "coca-cola\tsprite",
      },
    },
    features: [
      "baseline",
      "pairs",
      "images",
      "row-images",
      "restored",
      "dont-know",
    ],
  },
  {
    id: "grid-this-or-that-baseline",
    description: "Choose between soft drink pairs",
    type: "grid_this_or_that",
    request: {
      type: "grid_this_or_that",
      content: "Which drink would you choose from each pair?",
      row_options: [
        [
          {
            value: "Coca-Cola",
            text: "Coca-Cola",
            display_text: true,
            unique: true,
            disabled: false,
          },
          {
            value: "Pepsi",
            text: "Pepsi",
            display_text: true,
            unique: true,
            disabled: false,
          },
        ],
        [
          {
            value: "Sprite",
            text: "Sprite",
            display_text: true,
            unique: true,
            disabled: false,
            image: softDrinkImages.sprite,
          },
          {
            value: "Canada Dry",
            text: "Canada Dry",
            display_text: true,
            unique: true,
            disabled: false,
            image: softDrinkImages.canadaDry,
          },
        ],
      ],
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    previousAnswer: ["Pepsi", "Sprite"],
    expected: {
      response: {
        type: "grid_this_or_that",
        value: "Coca-Cola,Canada Dry",
        responses: "Coca-Cola\tCanada Dry",
      },
    },
    features: [
      "baseline",
      "pairs",
      "images",
      "option-images",
      "restored",
      "dont-know",
    ],
  },
  {
    id: "grid-this-or-that-rating-baseline",
    description: "Rate preference between drink pairs",
    type: "grid_this_or_that_rating",
    request: {
      type: "grid_this_or_that_rating",
      content: "For each pair, slide toward the drink you prefer.",
      image: drinkComparisonsImage,
      alt_text: "Two colas, lemon-lime soda, and ginger ale in a row",
      row_options: [
        [
          { value: 0, text: "Coca-Cola", display_text: true },
          { value: 1, text: "", display_text: false },
          { value: 2, text: "Pepsi", display_text: true },
        ],
        [
          { value: 0, text: "Sprite", display_text: true },
          { value: 1, text: "", display_text: false },
          { value: 2, text: "Canada Dry", display_text: true },
        ],
      ],
      dont_know_option: { value: "Don't know", text: "Don't know" },
    },
    previousAnswer: ["2", "Don't know"],
    expected: {
      response: {
        type: "grid_this_or_that_rating",
        value: "0,2",
        responses: "0\t2",
      },
    },
    features: [
      "baseline",
      "rows",
      "labels",
      "images",
      "restored",
      "dont-know",
      "unanswered",
      "non-one-based",
    ],
  },
  {
    id: "conjoint-baseline",
    description: "Choose between priced soft drinks",
    type: "conjoint",
    request: {
      type: "conjoint",
      content: "At these prices, which drink would you buy?",
      options: [
        {
          value: "001",
          text: "Coca-Cola Original",
          image: softDrinkImages.cocaCola,
          description: "Coca-Cola Original can",
          disabled: false,
          display_text: true,
        },
        {
          value: "002",
          text: "Pepsi Original",
          image: softDrinkImages.pepsi,
          disabled: true,
          display_text: true,
        },
      ],
      rows: [
        { value: "brand", text: "Brand", display_text: true },
        { value: "price", text: "Price", display_text: true },
      ],
      row_options: [
        [
          { value: "brand-a", text: "Coca-Cola" },
          { value: "brand-b", text: "Pepsi" },
        ],
        [
          { value: "price-a", text: "£1.50" },
          { value: "price-b", text: "£1.75" },
        ],
      ],
      none_option: { value: "none", text: "None of these" },
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    previousAnswer: "001",
    expected: { response: { type: "conjoint", value: "001" } },
    features: [
      "baseline",
      "concepts",
      "attributes",
      "images",
      "option-images",
      "restored",
      "none",
      "dont-know",
      "disabled",
      "numeric-looking-ids",
    ],
  },
  {
    id: "conjoint-long-labels",
    description: "Fit long connectivity labels without splitting words",
    type: "conjoint",
    request: {
      type: "conjoint",
      content: "Which option do you prefer?",
      options: [
        {
          value: "option-1",
          text: "Option 1",
          image: null,
          disabled: false,
          display_text: true,
        },
        {
          value: "option-2",
          text: "Option 2",
          image: null,
          disabled: false,
          display_text: true,
        },
        {
          value: "option-3",
          text: "Option 3",
          image: null,
          disabled: false,
          display_text: true,
        },
      ],
      rows: [
        {
          value: "connection",
          text: "How the connection gets into your home",
          display_text: true,
        },
        {
          value: "installation",
          text: "How long until you're connected",
          display_text: true,
        },
        { value: "speed", text: "Speed", display_text: true },
        {
          value: "requirements",
          text: "Set up requirements",
          display_text: true,
        },
        {
          value: "equipment",
          text: "Extra equipment",
          display_text: true,
        },
      ],
      row_options: [
        [
          {
            value: "fiber-1",
            text: "Fiber line installed by an AT&T Fiber or Verizon Fios technician visiting your home",
          },
          {
            value: "fiber-2",
            text: "Fiber line installed by an AT&T Fiber or Verizon Fios technician visiting your home",
          },
          {
            value: "wireless",
            text: "T-Mobile 5G Home Internet (the plug-in wireless service)",
          },
        ],
        [
          { value: "next-day", text: "Next day" },
          { value: "three-days", text: "Within 3 days" },
          { value: "same-day", text: "Same day" },
        ],
        [
          {
            value: "slower-1",
            text: "Slightly slower than the fastest services widely available today",
          },
          {
            value: "same-speed",
            text: "About the same as the fastest services widely available today",
          },
          {
            value: "slower-2",
            text: "Slightly slower than the fastest services widely available today",
          },
        ],
        [
          {
            value: "restart",
            text: "Must be restarted at about the same time each week",
          },
          {
            value: "clearance",
            text: "Must be kept at least 12 inches from any other electronic equipment",
          },
          { value: "no-setup", text: "No special set-up requirements" },
        ],
        [
          { value: "none", text: "No extra equipment in the home" },
          {
            value: "wall-unit-1",
            text: "A separate wall-mounted unit installed indoors",
          },
          {
            value: "wall-unit-2",
            text: "A separate wall-mounted unit installed indoors",
          },
        ],
      ],
      none_option: null,
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    expected: { response: { type: "conjoint", value: "option-1" } },
    features: ["concepts", "attributes", "dont-know", "long-labels"],
  },
  {
    id: "text-highlighter-baseline",
    description: "Highlight claims that drive purchase",
    type: "text_highlighter",
    request: {
      type: "text_highlighter",
      content: "Highlight the words that make this drink sound appealing.",
      extra_text: "Coca-Cola has a bold taste and a refreshing finish.",
      color: "#EAC844",
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    previousAnswer: ["bold", "refreshing"],
    expected: {
      response: {
        answerJson: ["Coca-Cola", "refreshing"],
        type: "text_highlighter",
        value: "Coca-Cola, refreshing",
        responses: "Coca-Cola\trefreshing",
      },
    },
    features: [
      "baseline",
      "selection",
      "overlap",
      "restored",
      "color",
      "unanswered",
      "dont-know",
    ],
  },
  {
    id: "image-baseline",
    description: "Review a can before continuing",
    type: "image",
    request: {
      type: "image",
      content: "Take a look at this Coca-Cola can.",
      image: softDrinkImages.cocaCola,
      alt_text: "Coca-Cola Original can",
      number_seconds: 0,
    },
    expected: { response: { type: "image", value: "ok" } },
    features: ["baseline", "images", "acknowledge"],
  },
  {
    id: "image-timed",
    description: "Review a can for two seconds",
    type: "image",
    request: {
      type: "image",
      content: "Take a close look at this Pepsi can.",
      image: softDrinkImages.pepsi,
      alt_text: "Pepsi can",
      number_seconds: 2,
    },
    expected: { response: { type: "image", value: "ok" } },
    features: ["images", "timed", "acknowledge"],
  },
  {
    id: "video-baseline",
    description: "Review a soft drink ad video",
    type: "video",
    request: {
      type: "video",
      style: "vdoCipher",
      content: "Watch this soft drink ad before answering.",
      start_message: "Play the video to begin",
      video: exampleVideoUrl,
      number_seconds: 0,
      post_data: "encrypted-gallery-sample",
    },
    expected: { response: { type: "video", value: "Video viewed" } },
    features: ["baseline", "playback"],
  },
  {
    id: "video-dial-bottom",
    description: "Rate a soft drink ad as it plays",
    type: "video",
    request: {
      type: "video",
      style: "vdoCipher_dial_bottom",
      content: "How appealing is this soft drink ad as you watch?",
      start_message: "Play the video to begin",
      video: exampleVideoUrl,
      number_seconds: 0,
      options: [
        { value: 0, text: "Not appealing", display_text: true },
        { value: 50, text: "Neutral", display_text: true },
        { value: 100, text: "Very appealing", display_text: true },
      ],
      playing_message: "Move the dial as your reaction changes",
      post_data: "encrypted-gallery-sample",
      default: 50,
    },
    expected: { response: { type: "video", value: "Video viewed" } },
    features: ["dial-bottom", "samples", "playback"],
  },
  {
    id: "video-segment",
    description: "Review a bounded segment of a soft drink ad",
    type: "video",
    request: {
      type: "video",
      style: "vdoCipher",
      content: "Watch this excerpt of the soft drink ad before answering.",
      start_message: "Play the video excerpt to begin",
      video: exampleVideoUrl,
      number_seconds: 0,
      start_position_ms: 2_000,
      end_position_ms: 5_000,
      post_data: "encrypted-gallery-sample",
    },
    expected: { response: { type: "video", value: "Video viewed" } },
    features: ["playback", "segment"],
  },
  {
    id: "video-dial-right",
    description: "Rate a timed soft drink ad",
    type: "video",
    request: {
      type: "video",
      style: "vdoCipher_dial_right",
      content: "How appealing is this soft drink ad as you watch?",
      start_message: "Play the video to begin",
      video: exampleVideoUrl,
      number_seconds: 1,
      options: [
        { value: 0, text: "Not appealing", display_text: true },
        { value: 100, text: "Very appealing", display_text: true },
      ],
      playing_message: "Move the dial as your reaction changes",
      post_data: "encrypted-gallery-sample",
      default: 50,
    },
    expected: { response: { type: "video", value: "Video viewed" } },
    features: ["dial-right", "samples", "timed", "playback"],
  },
  {
    id: "embed-youtube",
    description: "Review embedded soft drink creative",
    type: "embed",
    request: {
      type: "embed",
      content: "Review this soft drink campaign concept.",
      embed_code:
        '<iframe width="560" height="315" src="https://www.youtube.com/embed/2gcsgfzqN8k?controls=0" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>',
      number_seconds: 1,
    },
    expected: { response: { type: "embed", value: "Video viewed" } },
    features: ["baseline", "iframe", "timed", "acknowledge"],
  },
  {
    id: "phone-baseline",
    description: "Phone number for product research follow-up",
    type: "phone_number",
    request: {
      type: "phone_number",
      content: "What number can we text for a follow-up about soft drinks?",
      default_country: "GB",
    },
    previousAnswer: "447700900123",
    expected: {
      response: { type: "text", value: "+447700900123" },
    },
    features: ["baseline", "restored", "country", "prefix"],
  },
  {
    id: "numeric-baseline",
    description: "Weekly soft drink spend",
    type: "numeric",
    request: {
      type: "numeric",
      content: "About how much do you spend on soft drinks in a week?",
      input_prefix: "£",
      input_suffix: "per week",
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    previousAnswer: "0",
    expected: { response: { type: "numeric", value: "0" } },
    features: [
      "baseline",
      "blank",
      "zero",
      "restored",
      "adornments",
      "dont-know",
    ],
  },
  {
    id: "text-baseline",
    description: "Explain a soft drink choice",
    type: "text",
    request: {
      type: "text",
      content: "What would make you switch to a different soft drink?",
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    previousAnswer: "A lower price",
    expected: { response: { type: "text", value: "A lower price" } },
    features: ["baseline", "restored", "empty", "multiline", "dont-know"],
  },
  {
    id: "text-list-baseline",
    description: "Name three soft drinks bought recently",
    type: "text_list",
    request: {
      type: "text_list",
      content: "Name three soft drinks you have bought recently.",
      rows: ["1", "2", "3"].map((value) => ({
        value,
        text: value,
        image: null,
        display_text: true,
      })),
      min_options: 3,
      dont_know_option: { value: "dont-know", text: "Don't know" },
    },
    previousAnswer: ["Coca-Cola", "Pepsi", "Sprite"],
    expected: {
      response: {
        answerJson: ["Coca-Cola", "Pepsi", "Sprite"],
        type: "text_list",
        value: "total_list_count_remove",
        responses: "Coca-Cola\tPepsi\tSprite",
      },
    },
    features: [
      "baseline",
      "restored",
      "sequential",
      "simultaneous",
      "dont-know",
    ],
  },
  {
    id: "select-custom",
    description: "Name an unlisted soft drink",
    type: "select",
    request: {
      type: "select",
      content: "Which soft drink did you buy most recently?",
      options: [
        { value: 5, text: "Coca-Cola", disabled: false, display_text: true },
        {
          value: 90,
          text: "Other",
          disabled: false,
          display_text: true,
          pop_up_text: "Which other soft drink?",
        },
      ],
    },
    expected: {
      response: {
        answerJson: { custom: "Fanta" },
        type: "select",
        value: "Fanta",
      },
    },
    features: ["custom-text", "validation"],
  },
  {
    id: "select-rejected-custom",
    description: "Correct a retained Other answer",
    type: "select",
    request: {
      type: "select",
      content: "Which route?",
      error_message: "Please be more specific.",
      options: [
        { value: 1, text: "Train", disabled: false, display_text: true },
        {
          value: 9,
          text: "Other",
          disabled: false,
          display_text: true,
          pop_up_text: "Which other route?",
        },
      ],
    },
    previousAnswer: "Ferry",
    expected: {},
    features: ["custom-text", "restored", "validation"],
  },
  {
    id: "grid-select-row-specify",
    description: "Name an Other row and specify its answer",
    type: "grid_select",
    request: {
      type: "grid_select",
      content: "How often do you do this activity?",
      rows: [
        {
          value: "other-activity",
          text: "Other activity",
          display_text: true,
          pop_up_text: "Which activity?",
        },
      ],
      options: [
        { value: 1, text: "Often", disabled: false },
        {
          value: 9,
          text: "Other frequency",
          disabled: false,
          pop_up_text: "How often?",
        },
      ],
    },
    previousAnswer: [{ answer: "Every weekend", specified_row: "Kayaking" }],
    expected: {},
    features: ["row-custom-text", "custom-text", "restored"],
  },
  {
    id: "grid-multi-select-row-specify",
    description: "Name an Other row with multiple answers",
    type: "grid_multi_select",
    request: {
      type: "grid_multi_select",
      content: "When do you do this activity?",
      rows: [
        {
          value: "other-activity",
          text: "Other activity",
          display_text: true,
          pop_up_text: "Which activity?",
        },
      ],
      options: [
        { value: 1, text: "Weekdays", disabled: false },
        {
          value: 9,
          text: "Other time",
          disabled: false,
          pop_up_text: "When else?",
        },
      ],
    },
    previousAnswer: [
      { answer: ["1", "Every weekend"], specified_row: "Kayaking" },
    ],
    expected: {},
    features: ["row-custom-text", "custom-text", "restored"],
  },
  {
    id: "grid-rating-row-specify",
    description: "Name and rate an Other activity",
    type: "grid_rating",
    request: {
      type: "grid_rating",
      content: "Rate this activity.",
      rows: [
        {
          value: "other-activity",
          text: "Other activity",
          display_text: true,
          pop_up_text: "Which activity?",
        },
      ],
      options: [
        { value: 0, text: "Low", display_text: true },
        { value: 1, text: "", display_text: false },
        { value: 2, text: "High", display_text: true },
      ],
      dont_know_option: null,
    },
    previousAnswer: [{ answer: "0", specified_row: "Kayaking" }],
    expected: {},
    features: ["row-custom-text", "restored"],
  },
  {
    id: "grid-numeric-row-specify",
    description: "One row label across numeric cells and totals",
    type: "grid_numeric",
    request: {
      type: "grid_numeric",
      content: "How many hours did you spend?",
      columns: ["Weekdays", "Weekend"],
      autosum_rows: true,
      rows: [
        {
          value: "other-activity",
          text: "Other activity",
          display_text: true,
          pop_up_text: "Which activity?",
        },
      ],
    },
    previousAnswer: [
      [{ answer: "0", specified_row: "123" }],
      [{ answer: "2", specified_row: "123" }],
    ],
    expected: {},
    features: ["row-custom-text", "restored", "blank-zero", "autosum-rows"],
  },
  {
    id: "numeric-list-row-specify",
    description: "Name an Other item and enter zero or an amount",
    type: "grid_numeric",
    request: {
      type: "grid_numeric",
      content: "How many items?",
      autosum_columns: true,
      rows: [
        {
          value: "other-item",
          text: "Other item",
          display_text: true,
          pop_up_text: "Which item?",
        },
      ],
    },
    previousAnswer: [{ answer: "0", specified_row: "Kayaks" }],
    expected: {},
    features: ["row-custom-text", "restored", "blank-zero", "numeric-list"],
  },
  {
    id: "question-group-ordinary",
    description: "Ordinary mixed question group",
    type: "question_group",
    request: questionGroupRequest as QuestionGroupRaw,
    expected: { response: { type: "text", value: "Ada" } },
    features: ["baseline"],
  },
  {
    id: "grid-question-group",
    description: "Repeated records with mixed child controls",
    type: "grid_question_group",
    request: gridQuestionGroupRequest as GridQuestionGroupRaw,
    expected: { response: { type: "numeric", value: "8" } },
    features: ["baseline", "table", "cards", "validation"],
  },
  ...visualGroupFixtures,
] as const;
